import React from 'react';

import { DefaultContext } from '@apollo/client';

import { Nullable } from '@clearverify/types';

import {
  AccountPersonalInformationDocument,
  UserUpdate,
  useAccountPersonalInformationQuery,
  useAccountPersonalInformationUpdateMutation,
} from 'src/api';

export const useAccountPersonalInformation = (userId?: Nullable<string>) => {
  const { data: accountPersonalInformationGetResult, loading } = useAccountPersonalInformationQuery(
    {
      variables: {
        userId: String(userId),
      },
      skip: !userId,
    },
  );

  const [accountPersonalInformationUpdateMutation, { loading: updating }] =
    useAccountPersonalInformationUpdateMutation({
      refetchQueries: [AccountPersonalInformationDocument],
    });

  const updateAccountPersonalInformation = React.useCallback(
    async (params: { data: Omit<UserUpdate, 'id'>; context?: DefaultContext }) => {
      const { data, context } = params;
      try {
        const result = await accountPersonalInformationUpdateMutation({
          variables: {
            where: { id: userId },
            data,
          },
          context,
        });

        return result.data?.userUpdate;
      } catch (error) {
        console.error({ error });
        return undefined;
      }
    },
    [accountPersonalInformationUpdateMutation, userId],
  );

  const accountPersonalInformation = React.useMemo(
    () => accountPersonalInformationGetResult?.user,
    [accountPersonalInformationGetResult?.user],
  );

  return {
    accountPersonalInformation,
    updateAccountPersonalInformation,
    loading,
    updating,
  };
};
